import { ControlTypeAhead, ControlTypeAheadProps } from "../../controls/ControlTypeAhead";
import { getFromDataPath, isString } from "../../utility";
import { useArsContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARST_PATCH_DATA } from "../table";
import { useArsTable } from "../table/ArsTableContextProvider";
import { isUnlistedDatasetVariableValue } from "../utility/isUnlistedDatasetVariableValue";

/**
 * Props for the ArsVariable component.
 */
export interface ArsVariablePropsCombined extends Omit<
  ControlTypeAheadProps,
  "value" | "setValue" | "availableList"
> {
  /** RFC 6901 JSON Pointer path to the combined dataset.variable field in ArsContext state */
  combinedDataPath: string;
  /** Prevents passing separate paths when using combined path mode */
  datasetDataPath?: never;
  /** Prevents passing separate paths when using combined path mode */
  variableDataPath?: never;
  /** Allowed to change data set */
  canChangeDataset?: boolean;
  /** Indicates if the dataPath variables are in table data */
  tableDataPath?: boolean;
}
export interface ArsVariablePropsSeparate extends Omit<
  ControlTypeAheadProps,
  "value" | "setValue" | "availableList"
> {
  /** Prevents passing a combined path when using separate path mode */
  combinedDataPath?: never;
  /** RFC 6901 JSON Pointer path to the dataset field in ArsContext state */
  datasetDataPath: string;
  /** RFC 6901 JSON Pointer path to the variable field in ArsContext state */
  variableDataPath: string;
  /** Allowed to change data set */
  canChangeDataset?: boolean;
  /** Indicates if the dataPath variables are in table data */
  tableDataPath?: boolean;
}
export type ArsVariableProps = ArsVariablePropsCombined | ArsVariablePropsSeparate;

/**
 * Type guard for the combined path prop shape.
 *
 * @param props - ArsVariable props in either mode.
 * @returns True when props use a single combined data path.
 */
const isCombinedPathProps = (props: ArsVariableProps): props is ArsVariablePropsCombined =>
  "combinedDataPath" in props;

/**
 * A lightweight dataset/variable selector that reads from and writes to ArsContext state.
 * This is a thin wrapper around ControlTypeAhead that integrates with ArsContext.
 * Uses the variables list from ArsContext for typeahead functionality.
 *
 * When a variable is selected (e.g., "ADSL.AGE"), it automatically splits the value and updates
 * both the dataset field ("ADSL") and the variable field ("AGE") using a single dispatch with
 * multiple updates.
 *
 * @param props - Component props extending ControlTypeAheadProps.
 * @param props.canChangeDataset - Optional flag to allow changing the dataset.
 * @param props.combinedDataPath - RFC 6901 JSON Pointer path to the combined dataset.variable field in ArsContext state.
 * @param props.datasetDataPath - RFC 6901 JSON Pointer path to the dataset field in ArsContext state.
 * @param props.tableDataPath - Optional boolean indicating if the dataPath variables are in table data.
 * @param props.validCondition - Optional validation function for the combined dataset.variable value.
 * @param props.variableDataPath - RFC 6901 JSON Pointer path to the variable field in ArsContext state.
 * @returns A React functional component rendering a ControlTypeAhead integrated with ArsContext.
 */
export const ArsVariable = (props: ArsVariableProps): React.ReactElement => {
  const { canChangeDataset = true, tableDataPath = false, validCondition, ...rest } = props;
  const ars = useArsContext();
  const table = useArsTable();
  const sourceState = tableDataPath ? (table.state?.bodyData ?? {}) : ars.state;
  const isCombinedPath = isCombinedPathProps(props);

  // Get the current values from state using the dataPaths
  let combinedValue = "";
  let dataset = "";
  if (isCombinedPath) {
    combinedValue = getFromDataPath(props.combinedDataPath, sourceState, isString) ?? "";
    dataset = combinedValue.split(".")[0];
  } else {
    dataset = getFromDataPath(props.datasetDataPath, sourceState, isString) ?? "";
    const variable = getFromDataPath(props.variableDataPath, sourceState, isString) ?? "";
    combinedValue = dataset && variable ? `${dataset}.${variable}` : "";
  }

  /**
   * Handle selection change by splitting the value and dispatching updates to both paths.
   * Expected format: "DATASET.VARIABLE" (e.g., "ADSL.AGE")
   *
   * @param newValue - The new combined value in format "DATASET.VARIABLE".
   */
  const handleChange = (newValue: string): void => {
    // Split the value on the first period to separate dataset and variable
    const dotIndex = newValue.indexOf(".");
    let newDataset = "";
    let newVariable = "";

    if (isCombinedPath) {
      if (!tableDataPath) {
        ars.dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: props.combinedDataPath, value: newValue }],
        });
      } else {
        table.dispatch({
          operation: ARST_PATCH_DATA,
          updates: [{ dataPath: props.combinedDataPath, value: newValue }],
        });
      }
      return;
    }

    // When dataset changes are disabled and input is cleared, only clear variable.
    if (!canChangeDataset && newValue === "") {
      if (!tableDataPath) {
        ars.dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: props.variableDataPath, value: "" }],
        });
      } else {
        table.dispatch({
          operation: ARST_PATCH_DATA,
          updates: [{ dataPath: props.variableDataPath, value: "" }],
        });
      }
      return;
    }

    if (dotIndex > 0) {
      newDataset = newValue.substring(0, dotIndex);
      newVariable = newValue.substring(dotIndex + 1);
    } else {
      // If no period found, treat entire value as dataset
      newDataset = newValue;
    }

    if (!canChangeDataset) {
      // Ignore values that would change dataset or that are dataset-only.
      if (dotIndex <= 0 || newDataset !== dataset) {
        return;
      }

      if (!tableDataPath) {
        ars.dispatch({
          operation: PATCH_ARS_STATE,
          updates: [{ dataPath: props.variableDataPath, value: newVariable }],
        });
      } else {
        table.dispatch({
          operation: ARST_PATCH_DATA,
          updates: [{ dataPath: props.variableDataPath, value: newVariable }],
        });
      }
      return;
    }

    // Dispatch both updates atomically
    if (!tableDataPath) {
      ars.dispatch({
        operation: PATCH_ARS_STATE,
        updates: [
          { dataPath: props.datasetDataPath, value: newDataset },
          { dataPath: props.variableDataPath, value: newVariable },
        ],
      });
    } else {
      table.dispatch({
        operation: ARST_PATCH_DATA,
        updates: [
          { dataPath: props.datasetDataPath, value: newDataset },
          { dataPath: props.variableDataPath, value: newVariable },
        ],
      });
    }
  };

  const availableVariables = canChangeDataset
    ? ars.state.variables
    : ars.state.variables.filter((variable) => variable.dataset === dataset);

  return (
    <ControlTypeAhead
      {...rest}
      availableList={availableVariables}
      value={combinedValue}
      setValue={handleChange}
      validCondition={
        canChangeDataset
          ? // Allow unlisted variables when changing dataset
            isUnlistedDatasetVariableValue
          : // Uses standard validation for dataset.variable format when not changing dataset
            validCondition
      }
    />
  );
};

ArsVariable.displayName = "ArsVariable";
